import {
    createSelector,
} from '@reduxjs/toolkit';

import {
    isSelectionSuspended,
} from '~Betslip/utils/selectionChecks';
import {
    isCombinationSelection,
} from '~Betslip/utils/combinationsUtils';
import {
    shouldShowEW,
} from '~shared/components/sport-event/utils';

const selectOriginalSelectionDetails = state => state.betslip.selectionDetails;

const selectFrozenSelectionDetails = state => state.betslip.frozenSelectionDetails;

export const selectSelectionDetails = createSelector(
    [
        selectOriginalSelectionDetails,
        selectFrozenSelectionDetails,
    ],
    (originalSelectionDetails, frozenSelectionDetails) => (
        Object.keys(frozenSelectionDetails).length ? frozenSelectionDetails : originalSelectionDetails
    ),
);

export const selectSelectionDetailsById = createSelector(
    [
        selectSelectionDetails,
        (_state, selectionId) => selectionId,
    ],
    (selections, selectionId) => {
        if (!selections[selectionId]) {

            return null;
        }

        const selection = selections[selectionId];
        const suspended = !isCombinationSelection(selection) && isSelectionSuspended(selection);
        const currentSelection = {
            ...selections[selectionId],
            suspended,
        };

        return currentSelection;
    },
);

export const selectCountSelectionDetails = createSelector(
    [
        selectSelectionDetails,
    ],
    (details) => Object.keys(details).length,
);

export const selectSelectionEventMarketIds = createSelector(
    selectOriginalSelectionDetails,
    selectionDetails => Object.keys(selectionDetails).reduce((accSelections, id) => {
        const selection = selectionDetails[id];
        if (!isCombinationSelection(selection)) {
            const { event_id: eventId, market_id: marketId } = selection;
            accSelections[id] = { eventId, marketId };
        }

        return accSelections;
    }, {}),
);

/**
 * Mind the difference between combinations (meta selections) and normal (real) selections
 */
export const selectCountSelectionDetailsWithoutCombinations = createSelector(
    [
        selectSelectionDetails,
    ],
    (details) => Object.keys(details).filter(
        id => !isCombinationSelection((details[id])),
    ).length,
);

export const selectCountSuspendedSelections = createSelector(
    [
        selectSelectionDetails,
    ],
    (selections) => {
        return Object.values(selections).reduce((total, selection) => {
            const suspended = isSelectionSuspended(selection);

            if (suspended) {
                return total + 1;
            }

            return total;
        }, 0);
    }
);

export const selectAnySelectionSuspended = createSelector(
    [
        selectCountSuspendedSelections,
    ],
    (suspendedSelectionCount) => suspendedSelectionCount > 0
);

/**
 * A combination's underlying selection details
 */
export const selectOneCombinationSelectionsDetails = createSelector(
    (_state, id) => id,
    selectSelectionDetails,
    (combinationId, selectionsDetails) => {
        const selectionIds = selectionsDetails[combinationId]?.selection_ids || [];
        const combinationSelectionDetails = selectionIds.reduce(
            (acc, id) => {
                if (selectionsDetails[id]) {

                    return { ...acc, [id]: selectionsDetails[id] };
                }

                return acc;
            },
            {},
        );

        return combinationSelectionDetails;
    },
);

/**
 * Is any underlying selection suspended?
 */
export const selectOneCombinationSelectionsSuspended = createSelector(
    selectOneCombinationSelectionsDetails,
    (selectionObjects = []) => {
        const selections = Object.values(selectionObjects);

        return Boolean(selections.some(selection => isSelectionSuspended(selection)));
    },
);

export const selectHasInplay = createSelector(
    [
        selectSelectionDetails,
    ],
    (selections) =>
        Object.values(selections).some(selection => selection.is_inplay),
);

export const selectAllSelectionsAreSuspended = createSelector(
    [
        selectCountSelectionDetails,
        selectCountSuspendedSelections,
    ],
    (allSelectionsCount, suspendedCount) => (allSelectionsCount === suspendedCount),
);

export const selectSuspendedSelections = createSelector(
    [
        selectSelectionDetails,
    ],
    (selections) => {
        return Object.keys(selections).reduce((acc, selectionId) => {
            const selection = selections[selectionId];
            const isSuspended = isSelectionSuspended(selection);

            return isSuspended && !isCombinationSelection(selection)
                ? {
                    ...acc,
                    [selectionId]: selection,
                }
                : acc;
        }, {});
    },
);

export const selectHasOddsBoost = createSelector(
    [
        selectSelectionDetailsById,
    ],
    (selection) => (Boolean(selection?.is_odds_boost)),
);

export const selectShowEachWay = createSelector(
    [
        selectSelectionDetailsById,
    ],
    (selection) => (selection?.each_way_allowed && shouldShowEW(selection?.places_paid))
);

export const selectAllShowEachWay = createSelector(
    [
        selectSelectionDetails,
    ],
    (selectionDetails) => Object.values(selectionDetails).every(selection => shouldShowEW(selection?.places_paid))
);

export const selectHasForceEachWay = createSelector(
    [
        selectSelectionDetails,
    ],
    (selections) =>
        Object.values(selections).some(selection => selection.force_each_way),
);

// EW is forced for the combined Singles stake box only when every selection
// in the betslip is an EW Extra (force_each_way) selection.
export const selectIsEachWayForcedForSingles = createSelector(
    [
        selectSelectionDetails,
    ],
    (selectionDetails) => {
        const selections = Object.values(selectionDetails);
        const forceEachWayCount = selections.filter(selection => selection?.force_each_way).length;
        const regularSelectionsCount = selections.length - forceEachWayCount;

        return forceEachWayCount >= 2 && regularSelectionsCount === 0;
    },
);
